import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { colors } from '../../../colors';
import {
  selectDemoEndTimestamp,
  selectDemoExcludedBaseAssets,
  selectDemoStartTimestamp,
  setDemoEndTimestamp,
  setDemoStartTimestamp,
  toggleDemoBaseAsset,
} from '../../../store/slices/demo';
import { selectSubscriptionSummaries } from '../../../store/slices/subscription';
import { useDemoRebasedSubscriptions } from '../../../store/hooks/useDemoRebasedSubscriptions';

/** ISO date input ('YYYY-MM-DD') ⇄ epoch ms. */
const toDateInput = (ts: number | null): string => {
  if (ts == null) return '';
  const d = new Date(ts);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const fromDateInput = (value: string, endOfDay: boolean): number | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return endOfDay
    ? Date.UTC(y, m - 1, d, 23, 59, 59, 999)
    : Date.UTC(y, m - 1, d, 0, 0, 0, 0);
};

const labelStyle: CSSProperties = {
  fontSize: 10,
  color: '#7a7a7a',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  letterSpacing: '0.15em',
  fontWeight: 700,
  textTransform: 'uppercase',
};
const inputStyle: CSSProperties = {
  width: '100%',
  background: '#0d0d0d',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 3,
  color: '#e6e6e6',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
  padding: '6px 8px',
  colorScheme: 'dark',
  boxSizing: 'border-box',
};

const baseButtonStyle: CSSProperties = {
  fontFamily: 'Kanit, sans-serif',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  padding: '7px 14px',
  borderRadius: 3,
  transition:
    'background 0.12s ease, border-color 0.12s ease, color 0.12s ease',
};
const primaryButtonStyle: CSSProperties = {
  ...baseButtonStyle,
  background: colors.green,
  color: '#000',
  border: '1px solid transparent',
};
const secondaryButtonStyle: CSSProperties = {
  ...baseButtonStyle,
  background: 'transparent',
  color: '#a0a0a0',
  border: '1px solid rgba(255,255,255,0.20)',
};

const SettingsDropdown = () => {
  const dispatch = useDispatch();
  const startTs = useSelector(selectDemoStartTimestamp);
  const endTs = useSelector(selectDemoEndTimestamp);
  const excluded = useSelector(selectDemoExcludedBaseAssets);
  // Full sub list (for the toggle list — even excluded ones must show so the
  // user can re-enable them) AND the rebased map (for accurate windowed % of
  // the included assets).
  const rawSummaries = useSelector(selectSubscriptionSummaries);
  const { byBaseAsset: rebased } = useDemoRebasedSubscriptions();

  // Local draft of the date inputs. Re-rendering on every keystroke (as the
  // controlled-from-Redux approach did) broke year editing in <input
  // type="date"> because each digit replaced the field's intermediate value.
  // Keep editing local, dispatch only on Confirm.
  const [draftStart, setDraftStart] = useState<string>(toDateInput(startTs));
  const [draftEnd, setDraftEnd] = useState<string>(toDateInput(endTs));
  useEffect(() => {
    setDraftStart(toDateInput(startTs));
  }, [startTs]);
  useEffect(() => {
    setDraftEnd(toDateInput(endTs));
  }, [endTs]);

  const dateDirty =
    draftStart !== toDateInput(startTs) || draftEnd !== toDateInput(endTs);
  const dateActive = !!(startTs || endTs || draftStart || draftEnd);

  const handleConfirmDates = () => {
    dispatch(setDemoStartTimestamp(fromDateInput(draftStart, false)));
    dispatch(setDemoEndTimestamp(fromDateInput(draftEnd, true)));
  };
  const handleResetDates = () => {
    setDraftStart('');
    setDraftEnd('');
    dispatch(setDemoStartTimestamp(null));
    dispatch(setDemoEndTimestamp(null));
  };

  const excludedSet = useMemo(() => new Set(excluded), [excluded]);

  // Toggle list shows every subscription (excluded or not) so the user can
  // re-enable disabled ones. The percentage column is computed against the
  // CURRENTLY-INCLUDED rebased subs only, so when the date window or
  // exclusion list changes the numbers re-balance to sum to 100% across the
  // visible portfolio.
  const sortedSubs = useMemo(() => {
    const includedTotal = rawSummaries.reduce((acc, sub) => {
      if (excludedSet.has(sub.baseAsset)) return acc;
      const total =
        rebased[sub.baseAsset]?.totalBalanceInStable ??
        sub.totalBalanceInStable ??
        0;
      return acc + total;
    }, 0);

    return [...rawSummaries]
      .map((sub) => {
        const total =
          rebased[sub.baseAsset]?.totalBalanceInStable ??
          sub.totalBalanceInStable ??
          0;
        const included = !excludedSet.has(sub.baseAsset);
        return {
          baseAsset: sub.baseAsset,
          botFriendlyName: sub.botFriendlyName,
          total,
          pct: included && includedTotal > 0 ? total / includedTotal : 0,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [rawSummaries, rebased, excludedSet]);

  return (
    <div className="apex-drop" role="menu" style={{ width: 460 }}>
      <div className="apex-drop-header">
        <div className="apex-drop-id">
          <div className="apex-drop-name">Demo Settings</div>
          <div className="apex-drop-email">Filter the demo dashboard</div>
        </div>
      </div>

      {/* Date range */}
      <div className="apex-drop-label">Date range</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          padding: '4px 14px 8px',
        }}
      >
        <div>
          <div style={{ ...labelStyle, marginBottom: 4 }}>From</div>
          <input
            type="date"
            value={draftStart}
            max={draftEnd || undefined}
            onChange={(e) => setDraftStart(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <div style={{ ...labelStyle, marginBottom: 4 }}>To</div>
          <input
            type="date"
            value={draftEnd}
            min={draftStart || undefined}
            onChange={(e) => setDraftEnd(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          padding: '0 14px 14px',
        }}
      >
        <SecondaryButton
          label="Reset"
          enabled={dateActive}
          onClick={handleResetDates}
        />
        <PrimaryButton
          label="Confirm"
          enabled={dateDirty}
          onClick={handleConfirmDates}
        />
      </div>

      <div className="apex-drop-divider" />

      {/* Asset toggles */}
      <div className="apex-drop-label">
        Subscriptions ({sortedSubs.length - excludedSet.size} /{' '}
        {sortedSubs.length})
      </div>
      <div
        style={{
          maxHeight: 360,
          overflowY: 'auto',
          padding: '0 14px 14px',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gridAutoFlow: 'row',
          gap: 6,
        }}
      >
        {sortedSubs.map((sub) => (
          <SubscriptionToggle
            key={sub.baseAsset}
            baseAsset={sub.baseAsset}
            label={sub.botFriendlyName}
            pct={sub.pct}
            isOn={!excludedSet.has(sub.baseAsset)}
            onToggle={() => dispatch(toggleDemoBaseAsset(sub.baseAsset))}
          />
        ))}
      </div>
    </div>
  );
};

interface DateButtonProps {
  label: string;
  enabled: boolean;
  onClick: () => void;
}

const PrimaryButton = ({ label, enabled, onClick }: DateButtonProps) => {
  const [hovered, setHovered] = useState(false);
  const active = enabled && hovered;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...primaryButtonStyle,
        background: active ? '#00C870' : colors.green,
        boxShadow: active ? '0 0 14px rgba(0,176,97,0.45)' : 'none',
        transform: active ? 'translateY(-1px)' : 'none',
        opacity: enabled ? 1 : 0.5,
        cursor: enabled ? 'pointer' : 'default',
      }}
    >
      {label}
    </button>
  );
};

const SecondaryButton = ({ label, enabled, onClick }: DateButtonProps) => {
  const [hovered, setHovered] = useState(false);
  const active = enabled && hovered;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...secondaryButtonStyle,
        background: active ? 'rgba(255,255,255,0.06)' : 'transparent',
        borderColor: active ? '#a0a0a0' : 'rgba(255,255,255,0.20)',
        color: active ? '#fff' : '#a0a0a0',
        opacity: enabled ? 1 : 0.5,
        cursor: enabled ? 'pointer' : 'default',
      }}
    >
      {label}
    </button>
  );
};

interface ToggleProps {
  baseAsset: string;
  label: string;
  pct: number;
  isOn: boolean;
  onToggle: () => void;
}

const SubscriptionToggle = ({ label, pct, isOn, onToggle }: ToggleProps) => {
  const [hovered, setHovered] = useState(false);
  const borderColor = isOn
    ? hovered
      ? colors.green
      : `${colors.green}55`
    : hovered
    ? 'rgba(255,255,255,0.32)'
    : 'rgba(255,255,255,0.10)';
  const background = isOn
    ? hovered
      ? 'rgba(0,176,97,0.14)'
      : 'rgba(0,176,97,0.06)'
    : hovered
    ? 'rgba(255,255,255,0.04)'
    : 'transparent';
  const textColor = isOn ? '#fff' : hovered ? '#cfcfcf' : '#7a7a7a';
  const pctColor = isOn
    ? hovered
      ? '#ddd'
      : '#a0a0a0'
    : hovered
    ? '#888'
    : '#555';

  return (
    <button
      type="button"
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '8px 10px',
        border: `1px solid ${borderColor}`,
        borderRadius: 3,
        background,
        color: textColor,
        cursor: 'pointer',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: 12,
        transition:
          'background 0.12s ease, border-color 0.12s ease, color 0.12s ease',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: isOn ? colors.green : '#3a3a3a',
            boxShadow: isOn && hovered ? `0 0 6px ${colors.green}` : 'none',
            transition: 'box-shadow 0.12s ease',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
      </span>
      <span style={{ color: pctColor, flexShrink: 0, marginLeft: 8 }}>
        {(pct * 100).toFixed(1)}%
      </span>
    </button>
  );
};

export default SettingsDropdown;
